/**
 * WhatsApp Broadcast Success Page
 * 
 * Página de confirmación después del pago exitoso.
 * Muestra el link al canal de WhatsApp.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2 } from "lucide-react";

import { BRAND_FAN_URL } from "@/lib/brandConfig";

// ✨ Formulario de Tally para agregar al canal de WhatsApp
// El usuario completa su username de Instagram y Tally lo agrega automáticamente
const TALLY_FORM_URL = `${BRAND_FAN_URL}/r/igcanal`;
const AUTO_REDIRECT_DELAY = 3000; // 3 segundos

export default function WhatsAppBroadcastSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);

  const paymentId = searchParams.get("payment_id");
  const status = searchParams.get("status");

  // Redirect automático a WhatsApp
  useEffect(() => {
    if (!isVerifying && !error) {
      // Countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-redirect después del delay
      const redirectTimer = setTimeout(() => {
        window.location.href = TALLY_FORM_URL;
      }, AUTO_REDIRECT_DELAY);

      return () => {
        clearInterval(countdownInterval);
        clearTimeout(redirectTimer);
      };
    }
  }, [isVerifying, error]);

  useEffect(() => {
    // Verificar el pago con MercadoPago
    const verifyPayment = async () => {
      if (!paymentId) {
        setError("No se encontró el ID de pago");
        setIsVerifying(false);
        return;
      }

      if (status !== "approved") {
        setError("El pago no fue aprobado. Por favor intentá nuevamente.");
        setIsVerifying(false);
        return;
      }

      try {
        // Aquí podrías verificar el pago con tu backend si querés
        // Por ahora, confiamos en el status de MercadoPago
        
        // Simular verificación
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setIsVerifying(false);
      } catch (err) {
        console.error("[WhatsAppBroadcastSuccess] Error:", err);
        setError("Error al verificar el pago");
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [paymentId, status]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-purple-950/20 to-black text-white flex items-center justify-center">
        <div className="text-center px-4">
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin mx-auto mb-4 text-purple-500" />
          <p className="text-base sm:text-lg text-white/70">Verificando tu pago...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-purple-950/20 to-black text-white flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center mx-auto mb-5 sm:mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          
          <h1 className="text-xl sm:text-2xl font-bold mb-4">Hubo un problema</h1>
          <p className="text-sm sm:text-base text-white/70 mb-8">{error}</p>
          
          <button
            onClick={() => navigate("/whatsapp-broadcast")}
            className="w-full sm:w-auto bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3 px-6 rounded-xl transition-all"
          >
            Intentar nuevamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-950/20 to-black text-white">
      <div className="container mx-auto px-4 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-green-500 to-green-600 mb-6 sm:mb-8 animate-bounce-slow">
            <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            ¡Pago exitoso! 🎉
          </h1>

          <p className="text-base sm:text-lg text-white/70 mb-6 sm:mb-8">
            Tu suscripción fue confirmada. Completá el formulario para ingresar al canal.
          </p>

          {/* Payment Info */}
          {paymentId && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6 sm:mb-8 inline-block max-w-full">
              <p className="text-sm text-white/60">ID de pago</p>
              <p className="font-mono text-sm break-all">{paymentId}</p>
            </div>
          )}

          {/* Form Auto-Redirect Card */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-xl sm:rounded-2xl p-5 sm:p-8 mb-6 sm:mb-8">
            <div className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 mx-auto mb-5 sm:mb-6 animate-pulse">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold mb-3">Un paso más...</h2>
            <p className="text-sm sm:text-base text-white/70 mb-6">
              Te redirigimos al formulario en <span className="font-bold text-white">{countdown}</span> {countdown === 1 ? 'segundo' : 'segundos'}
            </p>

            {/* Botón para abrir inmediatamente */}
            <a
              href={TALLY_FORM_URL}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold py-3.5 sm:py-4 px-6 sm:px-8 rounded-xl transition-all shadow-lg hover:shadow-xl mb-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Completar formulario
            </a>

            <p className="text-xs text-white/50">
              O esperá y te redirigimos automáticamente
            </p>
          </div>

          {/* Quick Instructions */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 sm:p-6 mb-8">
            <h3 className="font-semibold mb-3 flex items-center justify-center gap-2">
              <span className="text-purple-400">💡</span>
              Próximos pasos
            </h3>
            <ol className="text-sm text-white/70 text-left space-y-2 max-w-sm mx-auto">
              <li className="flex items-start gap-2">
                <span className="font-bold text-white">1.</span>
                <span>Completá el formulario con tu <span className="font-semibold text-white">username de Instagram</span></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-white">2.</span>
                <span>Te agregaremos al canal de WhatsApp automáticamente</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-white">3.</span>
                <span>Empezá a recibir contenido exclusivo en WhatsApp</span>
              </li>
            </ol>
          </div>

          {/* Footer */}
          <button
            onClick={() => navigate("/")}
            className="text-white/60 hover:text-white transition-colors text-sm"
          >
            ← Volver al inicio
          </button>
        </div>
      </div>
    </div>
  );
}
